import { FC, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { DropdownMenuIndicator } from "~/application/components/DropdownMenu";
import {
  expenseCardMenuItems,
  expensesMenuItems,
  notificationsMenuItems,
  orderMenuItems,
  reportingCenterMenuItems,
  searchMenuItems,
  settingsMenuItems,
} from "~/application/data/menuItems";
import { routes } from "~/application/theme/routes";
import { NavMenuModule, filterMenuItems } from "~/application/utils/menu-functions";
import { Flex } from "~/components/Flex";
import { Icon } from "~/components/Icon";
import { SvgChevronDown, SvgPerson } from "~/components/Icon/icons";
import { Text } from "~/components/Text";
import { useUser } from "~/presentation/core/contexts/UserContext";
import { Actions, AgencyEmployeeType, ModelTypeEnum, UserContext } from "~/application/types";
import { NavMenuItem } from "./NavMenuItem";
import { useVerifyActions } from "~/presentation/shared/hooks/useVerifyActions";
import { IconButton } from "~/components/IconButton";
import { customerService } from "~/application/usecases";
import { useQuery } from "@tanstack/react-query";
import { logError } from "~/presentation/shared/utils/errors";
import { QueryTimes } from "~/constants";
import { dialogService } from "~/components/DialogStack";
import { AdditionalInfo } from "~/core/modules/Agency/pages/AgencyOrderPage/components/AdditionalInfoDialog";
import { CustomerInfoDialog } from "~/core/modules/Agency/pages/AgencyOrderPage/components/CustomerInfoDialog";
import { getServiceIcon } from "~/presentation/shared/utils";
import { AdditionalInfoProps } from "~/core/modules/Agency/pages/AgencyOrderPage";
import { Skeleton } from "~/components/Skeleton";
import { useTheme } from "~/containers/DynamicThemeProvider";

const LOAD_CUSTOMER_ADITIONALINFO_ERROR_MESSAGE = "Erro ao carregar informações do cliente";

const LOG_TAG = "Customer/CustomerPage/useCostCenters";

export const NavMenu: FC = () => {
  const { user, userActions, profile, contexts } = useUser();
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get("orderType");
  const isAdvance = orderType === ModelTypeEnum.ADVANCE;
  const isTravel = orderType === ModelTypeEnum.TRAVEL;
  const isExpenseReport = orderType === ModelTypeEnum.EXPENSE_REPORT;
  if (!profile && user.context !== UserContext.Agency && user.context !== UserContext.Biztrip)
    return;

  const activeMenu = useMemo<NavMenuModule>(() => {
    if (location.pathname.startsWith("/busca")) return "search";
    if (location.pathname.startsWith("/despesas/solicitar-adiantamento")) return "search";
    if (location.pathname.startsWith("/pedidos/meus-pedidos")) {
      if (isAdvance) return "advance";
      if (isTravel) return "order";
      if (isExpenseReport) return "expense-report";
    }
    if (location.pathname.startsWith("/despesas/lancar-despesas")) return "search";
    if (location.pathname.startsWith("/despesas")) return "expenses";
    if (location.pathname.startsWith("/configuracoes")) return "settings";
    if (location.pathname.startsWith("/notificacoes")) return "notifications";
    if (location.pathname.startsWith("/dashboard-bi")) return "dashboard";
    if (location.pathname.startsWith("/central-de-relatorios")) return "reporting-center";
    if (location.pathname.startsWith("/cartoes")) return "card";
  }, [location.pathname, orderType, isAdvance, isTravel, isExpenseReport]);

  const { data: additionalInfo, isLoading: isLoadingAdditionalInfo } = useQuery(
    [contexts.customer],
    async () =>
      await customerService.findById(contexts?.customer.uuid ?? "", {
        withApprovers: true,
        withIssuers: true,
        withAdditionalInfo: true,
      }),
    {
      staleTime: QueryTimes.SMALLEST,
      refetchOnMount: "always",
      enabled: !!contexts.customer && !!contexts.agency,
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: LOAD_CUSTOMER_ADITIONALINFO_ERROR_MESSAGE,
        });
      },
    },
  );

  const dialogAdditionalInfo = useCallback(({ label, obs }: AdditionalInfoProps) => {
    return dialogService.showDialog(<AdditionalInfo label={label} obs={obs} />);
  }, []);

  const customerInfoDialog = useCallback(() => {
    return dialogService.showDialog(
      <CustomerInfoDialog
        approvers={additionalInfo?.approvers}
        issuers={additionalInfo?.issuers}
      />,
    );
  }, [additionalInfo]);

  const dashboardMenus = useMemo(() => {
    const canShowDashboard = useVerifyActions({
      actions: [Actions.ViewDashboard],
      contexts,
      profile,
    });

    if (!canShowDashboard) return;

    return (
      <Flex onClick={() => navigate(routes.DashboardBIV2.root)}>
        <NavMenuItem
          isActive={activeMenu === "dashboard"}
          menuItems={[]}
          color={colors["nav-menu-text"]}
        >
          <Text>Dashboard</Text>
        </NavMenuItem>
      </Flex>
    );
  }, [activeMenu, userActions, contexts, user, navigate]);

  const crmMenus = useMemo(() => {
    const isManager = user.type === AgencyEmployeeType.MANAGER;
    const isValidAgencyEmployee = isManager && user.context === UserContext.Agency;

    if (isValidAgencyEmployee && contexts.customer) {
      return !isLoadingAdditionalInfo ? (
        <Flex align="center">
          <IconButton size="lg" onClick={customerInfoDialog}>
            <Icon as={SvgPerson} />
          </IconButton>
          {Object.entries(additionalInfo?.additionalInfo || []).map(([key, value]) => (
            <IconButton
              key={key}
              size="lg"
              onClick={() => dialogAdditionalInfo({ label: key, obs: value.obs })}
            >
              <Icon as={getServiceIcon(key)} />
            </IconButton>
          ))}
        </Flex>
      ) : (
        <Flex align="center">
          <Skeleton css={{ width: "200px", height: "25px" }} />
        </Flex>
      );
    }

    return null;
  }, [activeMenu, userActions, contexts, user, additionalInfo, isLoadingAdditionalInfo]);

  const settingsMenus = useMemo(() => {
    const items = filterMenuItems(settingsMenuItems, userActions, contexts);
    const isManager = user.type === AgencyEmployeeType.MANAGER;
    const isValidAgencyEmployee = isManager && user.context === UserContext.Agency;
    const isValidCustomerEmployee = user.context === UserContext.Customer;
    const isIssuer = user.type === AgencyEmployeeType.ISSUER;

    if (isIssuer) {
      const companyItems = settingsMenuItems.filter(
        (si) =>
          si.href === routes.Configuration.Customers.root ||
          (si.title && si.title.toString().toLowerCase() === "empresas"),
      );

      const visibleCompanyItems = filterMenuItems(companyItems, userActions, contexts);
      if (visibleCompanyItems.length === 0) return;

      return (
        <NavMenuItem
          isActive={activeMenu === "settings"}
          menuItems={visibleCompanyItems}
          data-cy="settings-menu"
          color={colors["nav-menu-text"]}
        >
          <Text>Configurações</Text>
          <DropdownMenuIndicator>
            <Icon as={SvgChevronDown} />
          </DropdownMenuIndicator>
        </NavMenuItem>
      );
    }

    if (items.length && (isValidAgencyEmployee || isValidCustomerEmployee)) {
      return (
        <NavMenuItem
          isActive={activeMenu === "settings"}
          menuItems={items}
          data-cy="settings-menu"
          color={colors["nav-menu-text"]}
        >
          <Text>Configurações</Text>
          <DropdownMenuIndicator>
            <Icon as={SvgChevronDown} />
          </DropdownMenuIndicator>
        </NavMenuItem>
      );
    }
  }, [activeMenu, userActions, contexts, user, colors]);

  const notificationsMenu = useMemo(() => {
    const isBiztripUser = user.context === UserContext.Biztrip;
    if (!isBiztripUser) return;

    const items = filterMenuItems(notificationsMenuItems, userActions, contexts);
    if (items.length === 0) return;

    return (
      <NavMenuItem
        isActive={activeMenu === "notifications"}
        menuItems={items}
        data-cy="notifications-menu"
        color={colors["nav-menu-text"]}
      >
        <Text>Notificações</Text>
        <DropdownMenuIndicator>
          <Icon as={SvgChevronDown} />
        </DropdownMenuIndicator>
      </NavMenuItem>
    );
  }, [activeMenu, userActions, contexts, user, colors]);

  const searchMenus = useMemo(() => {
    const items = filterMenuItems(searchMenuItems, userActions, contexts);

    if (items.length === 0) return;

    return (
      <NavMenuItem
        isActive={activeMenu === "search"}
        menuItems={items}
        color={colors["nav-menu-text"]}
      >
        <Text>Início</Text>
        <DropdownMenuIndicator>
          <Icon as={SvgChevronDown} />
        </DropdownMenuIndicator>
      </NavMenuItem>
    );
  }, [activeMenu, userActions, contexts]);

  const orderMenus = useMemo(() => {
    const items = filterMenuItems(orderMenuItems, userActions, contexts, user);

    if (items.length === 0) return;

    return (
      <NavMenuItem
        isActive={activeMenu === "order"}
        menuItems={items}
        data-cy="order-menu"
        color={colors["nav-menu-text"]}
      >
        <Text>Pedidos</Text>
        <DropdownMenuIndicator data-cy="order-menu-indicator">
          <Icon as={SvgChevronDown} />
        </DropdownMenuIndicator>
      </NavMenuItem>
    );
  }, [activeMenu, userActions, contexts, user]);

  const expensesMenus = useMemo(() => {
    const items = filterMenuItems(expensesMenuItems, userActions, contexts);

    if (items.length === 0 || profile?.isExpenseApprover) return;

    return (
      <NavMenuItem
        color={colors["nav-menu-text"]}
        isActive={["expenses", "advance", "expense-report"].includes(activeMenu ?? "")}
        menuItems={items}
      >
        <Text>Despesas</Text>
        <DropdownMenuIndicator>
          <Icon as={SvgChevronDown} />
        </DropdownMenuIndicator>
      </NavMenuItem>
    );
  }, [activeMenu, userActions, profile, contexts]);

  const reportingCenterMenu = useMemo(() => {
    const canShowReportingCenter = useVerifyActions({
      actions: [Actions.ViewPurchaseReport],
      contexts,
      profile,
    });

    if (!canShowReportingCenter || !contexts?.customer) return;

    const items = filterMenuItems(reportingCenterMenuItems, userActions, contexts);

    if (items.length === 0) return;

    return (
      <NavMenuItem
        isActive={activeMenu === "reporting-center"}
        menuItems={items}
        color={colors["nav-menu-text"]}
      >
        <Text>Central de relatórios</Text>
        <DropdownMenuIndicator>
          <Icon as={SvgChevronDown} />
        </DropdownMenuIndicator>
      </NavMenuItem>
    );
  }, [activeMenu, userActions, contexts, profile]);

  const expenseCardMenus = useMemo(() => {
    const items = filterMenuItems(expenseCardMenuItems, userActions, contexts, user);

    if (!items.length) return;
    return (
      <NavMenuItem
        isActive={activeMenu === "card"}
        menuItems={items}
        color={colors["nav-menu-text"]}
      >
        <Text>Cartões</Text>
        <DropdownMenuIndicator>
          <Icon as={SvgChevronDown} />
        </DropdownMenuIndicator>
      </NavMenuItem>
    );
  }, [activeMenu, userActions, contexts, user, profile]);

  if (user.context === UserContext.Biztrip) {
    return (
      <Flex as="nav" gap="4" align="center">
        {notificationsMenu}
      </Flex>
    );
  }

  return (
    <Flex as="nav" gap="4" align="center">
      {searchMenus}
      {orderMenus}
      {expensesMenus}
      {settingsMenus}
      {expenseCardMenus}
      {reportingCenterMenu}
      {dashboardMenus}
      {crmMenus}
    </Flex>
  );
};

NavMenu.displayName = "NavMenu";
